import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

export const useFolders = (space = 'main') => {
  const noun = space === 'platinum_plus' ? 'Section' : 'Folder';
  const [folders, setFolders] = useState([]);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderForm, setFolderForm] = useState({
    module_id: '',
    name: '',
    description: '',
    order: 1,
    days_delay: 0
  });

  const fetchFolders = async () => {
    try {
      const response = await axios.get(`${API}/content-folders?space=${space}`);
      setFolders(response.data);
    } catch (error) {
      toast.error(`Failed to load ${noun.toLowerCase()}s`);
    }
  };

  const resetFolderForm = () => {
    setFolderForm({ module_id: '', name: '', description: '', order: 1, days_delay: 0 });
    setEditingFolder(null);
  };

  const handleEditFolder = (folder, scrollRef) => {
    setEditingFolder(folder.id);
    setFolderForm({
      module_id: folder.module_id,
      name: folder.name,
      description: folder.description,
      order: folder.order,
      days_delay: folder.days_delay || 0
    });
    scrollRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const saveFolder = async (onSuccess) => {
    if (!folderForm.module_id || !folderForm.name) {
      toast.error(`A ${noun.toLowerCase()} needs a name and somewhere to live`);
      return;
    }

    try {
      if (editingFolder) {
        await axios.put(`${API}/content-folders/${editingFolder}`, { ...folderForm, space });
        toast.success(`${noun} updated`);
      } else {
        await axios.post(`${API}/content-folders`, { ...folderForm, space });
        toast.success(`${noun} created`);
      }
      resetFolderForm();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to save ${noun.toLowerCase()}`);
    }
  };

  const deleteFolder = async (folderId, onSuccess) => {
    try {
      await axios.delete(`${API}/content-folders/${folderId}`);
      toast.success(`${noun} and its contents deleted`);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(`Failed to delete ${noun.toLowerCase()}`);
    }
  };

  return {
    folders,
    editingFolder,
    folderForm,
    setFolderForm,
    fetchFolders,
    resetFolderForm,
    handleEditFolder,
    saveFolder,
    deleteFolder
  };
};

export default useFolders;
